<template>
  <div>
    <p class="mb-2 text-base md:mb-3">
      {{ $t("sidebar.enterLicensePlate") }}
    </p>

    <p class="block mb-1 font-bold">{{ $t("common.licensePlate") }}</p>
    <div class="flex flex-wrap items-center w-full gap-x-3 gap-y-1">
      <div class="w-40 flex rounded" :class="[licensePlateLook.background]">
        <div
          class="flex flex-col flex-grow-0 flex-shrink-0 rounded-l justify-around items-center pb-1 text-xs tracking-tighter w-6 leading-none"
          :class="[
            licensePlateLook.asideBackground,
            licensePlateLook.asideTextColor
          ]"
        >
          <div>
            <img
              v-if="licensePlateLook.flagCode"
              :src="`/flags/${licensePlateLook.flagCode}.svg`"
              class="w-4"
            />
          </div>
          <span>{{ licensePlateLook.code }}</span>
        </div>
        <validation-provider
          name="License Plate"
          mode="eager"
          rules="required"
          v-slot="{ errors }"
        >
          <input
            class="block w-full form-input rounded-l-none rounded-r border-l-0 bg-transparent"
            v-model.trim="licensePlateNumber"
            type="text"
            style="border-left: 0;"
          />
          <span
            class="absolute block px-1 mt-1 text-xs text-red-100 bg-red-500 rounded-sm shadow-lg"
            >{{ errors[0] }}</span
          >
        </validation-provider>
      </div>
      <base-button
        class="w-auto"
        :loading="isLoading"
        :disabled="!licensePlateNumber"
        @click="handleSubmit(licensePlateNumber)"
      >
        {{ $t("sidebar.search") }}
      </base-button>
    </div>
    <transition name="fade">
      <div
        v-if="notFound"
        class="px-3 py-3 mt-4 text-teal-900 bg-gray-200 border-t-4 rounded-sm border-lightblue"
        role="alert"
      >
        <div class="flex">
          <div class="py-1">
            <svg
              class="w-5 h-5 mr-3 fill-current text-lightblue"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <path
                d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z"
              />
            </svg>
          </div>
          <div>
            <p class="font-bold">{{ $t("sidebar.notFound") }}</p>
          </div>
        </div>
      </div>
    </transition>
    <base-select
      v-if="
        Array.isArray(this.licensePlate.types) &&
          licensePlate.types.length > 1 &&
          licensePlate &&
          !notFound
      "
      :placeholder="$t('sidebar.type')"
      :label="$t('sidebar.type')"
      :options="licensePlate.types"
      @selected="setData"
      target="licensePlate"
    ></base-select>
  </div>
</template>

<script>
import BaseButton from '@/components/BaseButton.vue';
import BaseSelect from '@/components/BaseSelect.vue';
import { ValidationProvider, extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
import { mapActions, mapState, mapMutations } from 'vuex';
import { updateUrlParams } from '@/util/url-params';

extend('required', required);

export default {
  data: () => ({
    licensePlateNumber: process.env.NODE_ENV === 'development' ? '00 0000' : '',
    isLoading: false
  }),
  components: {
    BaseSelect,
    BaseButton,
    ValidationProvider
  },
  computed: {
    ...mapState({
      licensePlate: state => state.apiData.licensePlate,
      notFound: state => state.apiData.licensePlate.notFound
    }),
    ...mapState('advancedSearch', {
      selectedAdvancedSearchCountry: state => state.selectedCountry
    }),
    licensePlateLook () {
      /**
       * @typedef {Object} CountryCode
       * @property {string} code - The International vehicle registration code.
       * @property {string} flagCode - The flag code for the flag image.
       * @property {string} background - The background color of the plate
       * @property {string} asideBackground - The background color of the left aside
       * @property {string} asideTextColor - The text color of the left aside
       */

      /**
       * Default values for code and flagCode.
       * @type {CountryCode}
       */
      const defaults = {
        code: this.selectedAdvancedSearchCountry,
        flagCode: 'eu',
        background: 'bg-gray-100',
        asideBackground: 'bg-eublue',
        asideTextColor: 'text-white'
      };

      /**
       * Mapping of country codes to their respective code and flagCode.
       * @type {Object.<string, CountryCode>}
       */
      const countryCodes = {
        DK: { code: 'DK' },
        SE: { code: 'S' },
        ES: { code: 'E' },
        FI: { code: 'FIN' },
        NO: { code: 'N', flagCode: 'no' },
        GB: {
          code: 'UK',
          flagCode: false,
          background: 'bg-yellow',
          asideBackground: 'bg-yellow',
          asideTextColor: 'text-black'
        },
        NL: { code: 'NL', background: 'bg-yellow' },
        FR: {
          code: 'F'
        }
      };

      return {
        ...defaults,
        ...countryCodes[this.selectedAdvancedSearchCountry]
      };
    }
  },
  methods: {
    ...mapActions([
      'GET_TYPES_BY_LICENSEPLATE',
      'GET_RESULTS',
      'UNSET_SELECTED'
    ]),
    ...mapMutations(['SET_TYPES_BY_LICENSEPLATE', 'SET_SELECTED']),
    async setData (prop) {
      await this.UNSET_SELECTED({
        selected: ['vehicle', 'make', 'model', 'type'],
        data: ['makers', 'models', 'types', 'results']
      });
      await this.GET_RESULTS(prop.value);
    },
    async handleSubmit (licensePlate) {
      this.isLoading = true;
      this.SET_TYPES_BY_LICENSEPLATE([]);
      await this.UNSET_SELECTED({
        selected: ['vehicle', 'make', 'model', 'type'],
        data: ['makers', 'models', 'types', 'results']
      });
      try {
        await this.GET_TYPES_BY_LICENSEPLATE({ licensePlate });
        if (!this.notFound) {
          if (!Array.isArray(this.licensePlate.types)) {
            updateUrlParams('selectedType', this.licensePlate.types);
          } else {
            updateUrlParams('selectedType', this.licensePlate.types[0].value);
          }
        }
      } catch (err) {
        console.error(err);
      }
      this.isLoading = false;
    }
  }
};
</script>

<style lang="postcss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.form-input {
  &:focus {
    @apply bg-transparent;
  }
}
</style>
